import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HeaderProps } from '../types';

const LANGUAGES = [
  { code: 'es', flag: '🇪🇸', name: 'Español' },
  { code: 'pt', flag: '🇧🇷', name: 'Português' },
  { code: 'fr', flag: '🇫🇷', name: 'Français' },
  { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
  { code: 'id', flag: '🇮🇩', name: 'Indonesia' },
  { code: 'ph', flag: '🇵🇭', name: 'Filipino' },
  { code: 'th', flag: '🇹🇭', name: 'ไทย' },
  { code: 'vi', flag: '🇻🇳', name: 'Tiếng Việt' },
  { code: 'jp', flag: '🇯🇵', name: '日本語' },
  { code: 'kr', flag: '🇰🇷', name: '한국어' },
  { code: 'cn', flag: '🇨🇳', name: '中文' },
];

function Header({ theme, onCycleTheme }: HeaderProps): React.ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const langPaths = ['/', '/es', '/pt', '/fr', '/de', '/id', '/ph', '/th', '/vi', '/jp', '/kr', '/cn'];
  // Normalize the trailing slash so the selector renders identically in react-snap's
  // prerender (which crawls e.g. "/es/") and the client (canonical "/es") — otherwise
  // the selector appears only on the client and breaks hydration on language pages.
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  const showLangSelector = langPaths.includes(normalizedPath);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menu on route change (link click)
  const handleLinkClick = () => {
    setMenuOpen(false);
  };
  const getThemeIcon = (): React.ReactElement => {
    if (theme === 'auto') {
      // Auto/System icon
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
        </svg>
      );
    } else if (theme === 'light') {
      // Sun icon
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41l-1.06-1.06zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z"/>
        </svg>
      );
    } else {
      // Moon icon
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-2.98 0-5.4-2.42-5.4-5.4 0-1.81.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1z"/>
        </svg>
      );
    }
  };

  const getThemeLabel = (): string => {
    if (theme === 'auto') return 'Auto';
    if (theme === 'light') return 'Light';
    return 'Dark';
  };

  const getAriaLabel = (): string => {
    if (theme === 'light') return 'Theme: Light mode. Click to switch to Dark mode';
    if (theme === 'dark') return 'Theme: Dark mode. Click to switch to Auto (follow system)';
    return 'Theme: Auto (following system). Click to switch to Light mode';
  };

  return (
    <header className="header">
      <div className="container">
        <div className="header-content">
          <Link to="/" className="logo" aria-label="FancyMyText Home">
            <img src="/icons/icon-72x72.png" alt="FancyMyText logo" className="logo-icon" />
            <span className="logo-text">𝓕𝓪𝓷𝓬𝔂𝗠𝘆𝕋𝕖𝕩𝕥</span>
          </Link>

          <div className="header-actions">
            {showLangSelector && (
              <div className="language-selector" ref={langRef}>
                <button
                  className="language-toggle-btn"
                  onClick={() => setLangOpen(!langOpen)}
                  aria-expanded={langOpen}
                  aria-label="Select language"
                >
                  🌐
                </button>
                {langOpen && (
                  <div className="language-dropdown">
                    <Link to="/" className="language-option" onClick={() => setLangOpen(false)}>
                      <span className="language-flag">🇺🇸</span>
                      <span className="language-code">EN</span>
                    </Link>
                    {LANGUAGES.map((lang) => (
                      <Link
                        key={lang.code}
                        to={`/${lang.code}`}
                        className="language-option"
                        onClick={() => setLangOpen(false)}
                      >
                        <span className="language-flag">{lang.flag}</span>
                        <span className="language-code">{lang.code.toUpperCase()}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="header-menu" ref={menuRef}>
              <button
                className="menu-toggle-btn"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-expanded={menuOpen}
                aria-label="Menu"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
                </svg>
                <span>Menu</span>
              </button>
              {menuOpen && (
                <div className="header-dropdown">
                  <div className="dropdown-section">
                    <span className="dropdown-label">Generators</span>
                    <Link to="/bold-generator" onClick={handleLinkClick}>
                      <span className="dropdown-icon">𝗕</span>
                      Bold Generator
                    </Link>
                    <Link to="/stylish-name-generator" onClick={handleLinkClick}>
                      <span className="dropdown-icon">𝓝</span>
                      Stylish Names
                    </Link>
                    <Link to="/aesthetic-fonts" onClick={handleLinkClick}>
                      <span className="dropdown-icon">Ａ</span>
                      Aesthetic Fonts
                    </Link>
                    <Link to="/cool-fonts" onClick={handleLinkClick}>
                      <span className="dropdown-icon">𝔉</span>
                      Cool Fonts
                    </Link>
                    <Link to="/scifi-fonts" onClick={handleLinkClick}>
                      <span className="dropdown-icon">ᚱ</span>
                      Sci-Fi Fonts
                    </Link>
                    <Link to="/gaming-fonts" onClick={handleLinkClick}>
                      <span className="dropdown-icon">🎮</span>
                      Gaming Fonts
                    </Link>
                    <Link to="/birthday-fonts" onClick={handleLinkClick}>
                      <span className="dropdown-icon">🎂</span>
                      Birthday Fonts
                    </Link>
                  </div>
                  <div className="dropdown-section">
                    <span className="dropdown-label">Platforms</span>
                    <Link to="/instagram-fonts" onClick={handleLinkClick}>
                      <span className="dropdown-icon">📸</span>
                      Instagram Fonts
                    </Link>
                    <Link to="/facebook-fonts" onClick={handleLinkClick}>
                      <span className="dropdown-icon">👤</span>
                      Facebook Fonts
                    </Link>
                    <Link to="/discord-fonts" onClick={handleLinkClick}>
                      <span className="dropdown-icon">🎮</span>
                      Discord Fonts
                    </Link>
                  </div>
                  <div className="dropdown-section">
                    <span className="dropdown-label">Tools</span>
                    <Link to="/text-analyzer" onClick={handleLinkClick}>
                      <span className="dropdown-icon">#</span>
                      Character Counter
                    </Link>
                    <Link to="/case-converter" onClick={handleLinkClick}>
                      <span className="dropdown-icon">Aa</span>
                      Case Converter
                    </Link>
                    <Link to="/symbols" onClick={handleLinkClick}>
                      <span className="dropdown-icon">★</span>
                      Symbols & Emojis
                    </Link>
                    <Link to="/jawa-translator" onClick={handleLinkClick}>
                      <span className="dropdown-icon">⚙</span>
                      Jawa Translator
                    </Link>
                    <Link to="/number-to-words" onClick={handleLinkClick}>
                      <span className="dropdown-icon">123</span>
                      Number to Words
                    </Link>
                  </div>
                  <div className="dropdown-section">
                    <span className="dropdown-label">More</span>
                    <Link to="/blog" onClick={handleLinkClick}>
                      <span className="dropdown-icon">📝</span>
                      Blog
                    </Link>
                    <Link to="/app" onClick={handleLinkClick}>
                      <span className="dropdown-icon">📱</span>
                      Download App
                    </Link>
                    <Link to="/about" onClick={handleLinkClick}>
                      <span className="dropdown-icon">ℹ</span>
                      About
                    </Link>
                    <Link to="/contact" onClick={handleLinkClick}>
                      <span className="dropdown-icon">✉</span>
                      Contact
                    </Link>
                  </div>
                </div>
              )}
            </div>
            <button
              className="dark-mode-toggle"
              onClick={onCycleTheme}
              aria-label={getAriaLabel()}
              title={getAriaLabel()}
            >
              {getThemeIcon()}
              <span className="toggle-text">{getThemeLabel()}</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
